import type { ImageModelId } from './image-generation.types';
import type { ScreenComponentDef } from './slot-screen-component.types';

// ─── Screen IDs ───────────────────────────────────────────
export type SlotScreenId =
  | 'splash'
  | 'main-screen'
  | 'mega-win'
  | 'free-spin-trigger'
  | 'free-spin-process'
  | 'free-spin-end';

export const SLOT_SCREEN_ORDER: SlotScreenId[] = [
  'splash', 'main-screen', 'mega-win',
  'free-spin-trigger', 'free-spin-process', 'free-spin-end',
];

// ─── Screen Config ────────────────────────────────────────
export interface ScreenConfig {
  id: SlotScreenId;
  name: string;
  description: string;
  userPrompt: string;
  referenceImages: string[];
  generatedResults: ScreenResult[];
  selectedResultId: string | null;
  status: 'empty' | 'generating' | 'has-results' | 'selected';
  // Per-screen generation overrides (undefined = inherit global defaults)
  generationModel?: string;
  generationImageCount?: 1 | 2 | 4 | 8;
}

export interface ScreenResult {
  id: string;
  imageUrl: string;
  savedPath: string;
  status: 'generating' | 'ready' | 'error';
  error?: string;
  timestamp: number;
  prompt: string;
  model: string;
  width: number;
  height: number;
}

// ─── Idea Canvas (Multi-Session Chat) ─────────────────────

export type IdeaChatModelId =
  | 'gemini-3.1-pro-preview'
  | 'gemini-3-flash-preview'
  | 'gemini-3.1-flash-image-preview';

export type IdeaChatMode = 'chat' | 'create-image';

export interface IdeaChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  images: string[];                  // blob URLs at runtime
  savedImagePaths: string[];         // persisted relative paths on disk
  timestamp: number;
  mode?: IdeaChatMode;               // only on user messages
  thoughtSignature?: string;         // echoed back for Gemini 3 context
  thoughtText?: string;              // captured thinking content from Gemini
}

export interface IdeaChatSession {
  id: string;
  title: string;
  modelId: IdeaChatModelId;
  messages: IdeaChatMessage[];
  createdAt: number;
  updatedAt: number;
}

export interface IdeaCanvasState {
  sessions: IdeaChatSession[];
  activeSessionId: string | null;
  isGenerating: boolean;
  defaultModelId: IdeaChatModelId;
}

// ─── Video ────────────────────────────────────────────────
export type VideoCompilationStatus = 'idle' | 'compiling' | 'done' | 'error';

export type VideoFormat = 'webm' | 'mp4';
export type VideoModel = 'local' | 'veo';
export type VideoResolutionPreset = '16:9' | '9:16' | 'custom';

export interface VideoCompilationConfig {
  screenDurations: Record<SlotScreenId, number>;
  width: number;
  height: number;
  fps: number;
  format: VideoFormat;
  videoModel: VideoModel;
  totalDuration: number;
  resolutionPreset: VideoResolutionPreset;
}

export interface VideoCompilationState {
  status: VideoCompilationStatus;
  progress: number;
  outputUrl: string | null;
  error: string | null;
  currentGenerationId: string | null;
  currentScreenStatus: string | null;
}

// ─── Video Slot ────────────────────────────────────────────
export interface VideoSlotImage {
  screenId: SlotScreenId;
  imageUrl: string | null;
  source: 'generated' | 'upload';
  animationPrompt: string;
  animationSystemPrompt: string;
}

// ─── Video History ─────────────────────────────────────────
export interface VideoGenerationHistoryEntry {
  id: string;
  thumbnailUrl: string | null;
  videoUrl: string;
  savedPath: string;
  duration: number;
  resolution: { width: number; height: number };
  timestamp: number;
  status: 'generating' | 'ready' | 'error';
  error?: string;
}

// ─── Toast ────────────────────────────────────────────────
export interface ScreenSavedToast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
  timestamp: number;
}

// ─── Generation Settings ──────────────────────────────────
export interface ScreenGenerationSettings {
  model: string;
  imageCount: 1 | 2 | 4 | 8;
}

// ─── System Prompt Editor Preferences ────────────────────
export interface SystemPromptEditorPrefs {
  fontSize: number;
  tabSize: 2 | 4;
  lineNumbers: boolean;
  minimap: boolean;
  wordWrap: boolean;
}

// ─── Store State ──────────────────────────────────────────
export interface SlotScreenGeneratorState {
  systemPromptJson: string;
  isSystemPromptExpanded: boolean;
  systemPromptEditorPrefs: SystemPromptEditorPrefs;
  screens: Record<SlotScreenId, ScreenConfig>;
  activeScreenId: SlotScreenId;
  generationSettings: ScreenGenerationSettings;
  ideaCanvas: IdeaCanvasState;
  activeSection: 'screens-video' | 'screen-builder' | 'canvas';
  videoConfig: VideoCompilationConfig;
  videoState: VideoCompilationState;
  videoSlotImages: Record<SlotScreenId, VideoSlotImage>;
  videoHistory: VideoGenerationHistoryEntry[];
  fullViewScreenId: SlotScreenId | null;
  fullViewIndex: number | null;
  toasts: ScreenSavedToast[];
  // Screen Builder
  screenComponents: Record<SlotScreenId, ScreenComponentDef[]>;
  selectedComponentId: string | null;
  canvasZoom: number;
  generatorViewComponentId: string | null;
  generatorFullViewIndex: number | null;
}

// ─── Project Persistence ──────────────────────────────────
export interface SlotScreenGeneratorProjectState {
  version: number;
  lastModified: number;
  systemPromptJson: string;
  screens: Record<SlotScreenId, {
    userPrompt: string;
    selectedResultId: string | null;
    generationModel?: string;
    generationImageCount?: 1 | 2 | 4 | 8;
    generationHistory: Array<{
      id: string;
      savedPath: string;
      prompt: string;
      model: string;
      width: number;
      height: number;
      timestamp: number;
    }>;
  }>;
}
